import { Access, placeAllowed } from "../shared/access";
import { t } from "./i18n";

// one list of places. the rail, the dock, the launcher and the pane switches all read this

export interface PaneLink {
  id: string;
  label: string;
  view?: string;
}

export interface Place {
  id: string;
  group: string;
  view: string;
  label: string;
  title: string;
  icon: string;
  detail?: string;
  views?: string[];
  panes?: PaneLink[];
  // extra words the menu search matches, so "alarm" finds Calendar and "money" finds Budget
  words?: string;
  // desk only places are hidden on the website
  desk?: boolean;
}

export const PLACE_GROUPS = [
  { id: "desk", label: "desk" },
  { id: "make", label: "studio" },
  { id: "kit", label: "tools" },
  { id: "rack", label: "system" },
  { id: "web", label: "links" }
];

export const PLACES: Place[] = [
  { id: "today", group: "desk", view: "today", label: "today", title: "Today", icon: "today" },
  { id: "tasks", group: "desk", view: "tasks", label: "tasks", title: "Tasks", icon: "tasks" },
  { id: "routines", group: "desk", view: "routines", label: "routines", title: "Routines", icon: "repeat" },
  { id: "goals", group: "desk", view: "goals", label: "goals", title: "Goals", icon: "goal" },
  { id: "notes", group: "desk", view: "notes", label: "notes", title: "Notes", icon: "notes" },
  { id: "references", group: "desk", view: "references", label: "refs", title: "References", icon: "refs", panes: [
    { id: "scrap", label: "moodboard" },
    { id: "feed", label: "ref feed" },
    { id: "manual", label: "describe" }
  ] },
  { id: "profile", group: "desk", view: "profile", label: "me", title: "Profile", icon: "people", words: "avatar account alters" },
  { id: "achievements", group: "desk", view: "achievements", label: "awards", title: "Achievements", icon: "trophy", words: "medals points ladder" },
  { id: "chat", group: "desk", view: "chat", label: "ask", title: "Ask ChiX", icon: "ask" },
  { id: "challenge", group: "make", view: "challenge", label: "draw", title: "Draw today", icon: "doodle", words: "challenge daily exercise" },
  { id: "feed", group: "desk", view: "feed", label: "feed", title: "Feed", icon: "feed", words: "wall board chat community post everyone updates comments upskilling" },
  { id: "gesture", group: "make", view: "gesture", label: "gesture", title: "Gesture drawing", icon: "gesture" },
  { id: "timer", group: "make", view: "studio", detail: "timer", label: "timer", title: "Session timer", icon: "timer", words: "pomodoro focus stopwatch" },
  { id: "palette", group: "make", view: "studio", detail: "palette", label: "palette", title: "Palette", icon: "palette" },
  { id: "prompts", group: "make", view: "studio", detail: "prompts", label: "prompts", title: "Prompts", icon: "prompts" },
  { id: "jobs", group: "make", view: "studio", detail: "jobs", label: "comms", title: "Commissions", icon: "jobs", words: "jobs clients orders paid money" },
  { id: "budget", group: "desk", view: "budget", label: "budget", title: "Budget", icon: "budget", words: "money bills income costs spending" },
  { id: "japanese", group: "make", view: "japanese", label: "upskill", title: "Upskill", icon: "japanese", words: "japanese kanji languages cards study flashcards" },
  { id: "ocs", group: "make", view: "ocs", label: "ocs", title: "Characters", icon: "ocs", words: "oc original characters" },
  {
    id: "when",
    group: "kit",
    view: "calendar",
    views: ["calendar", "reminders"],
    label: "time",
    title: "Calendar",
    icon: "calendar",
    words: "reminders alarms pings events dates schedule",
    panes: [
      { id: "calendar", label: "month", view: "calendar" },
      { id: "reminders", label: "pings", view: "reminders" }
    ]
  },
  { id: "maps", group: "kit", view: "maps", label: "maps", title: "Maps", icon: "map" },
  { id: "calc", group: "kit", view: "calculator", label: "calc", title: "Calculator", icon: "calc", words: "math" },
  { id: "roulette", group: "kit", view: "roulette", label: "spin", title: "Roulette", icon: "roulette", words: "wheel random decide pick" },
  { id: "cat", group: "kit", view: "cat", label: "cat", title: "Cat mode", icon: "cat" },
  { id: "doodles", group: "make", view: "doodles", label: "doodles", title: "Doodles", icon: "doodle", words: "sketches drawings" },
  { id: "whiteboard", group: "make", view: "whiteboard", label: "boards", title: "Whiteboards", icon: "whiteboard", words: "draw together canvas sketch collab" },
  { id: "tierlists", group: "make", view: "tierlists", label: "tiers", title: "Tier lists", icon: "tierlist", words: "rank ranking" },
  { id: "tracker", group: "make", view: "tracker", label: "tracker", title: "Tracker", icon: "tracker", words: "music songs sound chiptune snes game audio" },
  { id: "music", group: "kit", view: "music", label: "music", title: "Music", icon: "music" },
  { id: "scan", group: "kit", view: "scan", label: "scans", title: "Scans", icon: "scan", words: "scanner paper import" },
  { id: "box", group: "rack", view: "server", label: "vps", title: "Server", icon: "server", words: "vps box restart deploy" },
  { id: "logs", group: "rack", view: "logs", label: "logs", title: "Logs", icon: "logs" },
  { id: "claude", group: "rack", view: "claude", label: "claude", title: "Claude usage", icon: "claude" },
  { id: "people", group: "rack", view: "people", label: "people", title: "People", icon: "people" },
  { id: "sharing", group: "rack", view: "sharing", label: "share", title: "Sharing", icon: "share" }
];

export const SETUP_PLACE: Place = {
  id: "settings",
  group: "",
  view: "settings",
  label: "setup",
  title: "Setup",
  icon: "settings",
  panes: [
    { id: "sys", label: "keys" },
    { id: "look", label: "themes" }
  ]
};

// first places in the saved order sit in the phone dock
export const DOCK_SIZE = 4;
export const DEFAULT_DOCK = ["today", "references", "chat", "notes"];

export const KNOWN_VIEWS = [
  "today", "tasks", "routines", "goals", "notes", "references", "chat", "reminders", "studio", "budget", "gesture", "japanese",
  "calendar", "maps", "calculator", "roulette", "cat", "scan", "doodles", "whiteboard", "tierlists", "tracker", "music", "server", "logs", "claude", "people", "sharing", "ocs", "settings", "web", "profile", "achievements", "challenge", "feed"
];

// places that were folded into another one, so an old link or a remembered tab still lands somewhere
export const RENAMED_VIEWS: Record<string, string> = { board: "feed" };

export function placeForView(view: string, detail = ""): Place | undefined {
  if (view === SETUP_PLACE.view) return SETUP_PLACE;
  const matches = PLACES.filter((place) => place.view === view || place.views?.includes(view));
  if (detail) {
    const hit = matches.find((place) => place.detail === detail || place.id === detail || place.panes?.some((pane) => pane.id === detail || pane.view === view));
    if (hit) return hit;
  }
  return matches[0];
}

export function placeById(id: string): Place | undefined {
  return id === SETUP_PLACE.id ? SETUP_PLACE : PLACES.find((place) => place.id === id);
}

export function defaultDetail(place: Place, requested = ""): string {
  if (requested) return requested;
  if (place.detail) return place.detail;
  if (place.panes?.length) return place.panes[0].id;
  return "";
}

export function openTarget(place: Place, paneId: string): { view: string; detail: string } {
  const pane = place.panes?.find((item) => item.id === paneId);
  if (pane?.view) return { view: pane.view, detail: "" };
  return { view: place.view, detail: paneId || defaultDetail(place) };
}

// chi's pocket radio: seven stations, each one a handful of places. the tuner, the mode knob and the rail read this.
// chat sits outside the stations, it is chi herself
export interface Station {
  id: string;
  label: string;
  freq: string;
  icon: string;
  // one line under the name in the station sheet
  blurb: string;
  places: string[];
}

// every place sits in the station where you would look for it. a place is listed once
export const STATIONS: Station[] = [
  { id: "home", label: "Home", freq: "88.1", icon: "today", blurb: "today at a glance", places: ["today", "feed", "achievements", "profile"] },
  { id: "plan", label: "Plan", freq: "89.4", icon: "calendar", blurb: "what to do and when", places: ["tasks", "notes", "routines", "goals", "when"] },
  { id: "studio", label: "Studio", freq: "91.7", icon: "palette", blurb: "make art, keep your world", places: ["references", "ocs", "whiteboard", "tracker", "doodles", "tierlists", "palette"] },
  { id: "practice", label: "Practice", freq: "93.2", icon: "gesture", blurb: "get better every day", places: ["challenge", "gesture", "timer", "prompts", "japanese"] },
  { id: "money", label: "Money", freq: "95.5", icon: "jobs", blurb: "commissions and budget", places: ["jobs", "budget"] },
  { id: "tools", label: "Tools", freq: "97.9", icon: "calc", blurb: "small handy things", places: ["maps", "music", "scan", "calc", "roulette", "cat", "web"] },
  { id: "core", label: "Core", freq: "99.0", icon: "settings", blurb: "run the app", places: ["people", "sharing", "box", "logs", "claude", "settings"] }
];

// someone fractal shares chix with only gets the places the people dashboard gave them.
// runs once at boot before anything paints, stations left with nothing to show drop off the dial
export function limitPlaces(access: Access | null): void {
  if (!access) return;
  for (let at = PLACES.length - 1; at >= 0; at--) {
    if (!placeAllowed(PLACES[at].id, access)) PLACES.splice(at, 1);
  }
  for (let at = STATIONS.length - 1; at >= 0; at--) {
    const station = STATIONS[at];
    station.places = station.places.filter((id) => id === "web" || id === SETUP_PLACE.id || PLACES.some((place) => place.id === id));
    if (!station.places.length) STATIONS.splice(at, 1);
  }
}

// web tabs all count as the "web" place of the library
export function stationForPlace(placeId: string): Station | undefined {
  const id = placeId.startsWith("web:") ? "web" : placeId;
  return STATIONS.find((station) => station.places.includes(id));
}

// names on the rail, dock, launcher and dial in the person's language, once at boot before anything paints
export function localizePlaces(): void {
  for (const group of PLACE_GROUPS) group.label = t(group.label);
  for (const place of [...PLACES, SETUP_PLACE]) {
    place.label = t(place.label);
    place.title = t(place.title);
    place.panes?.forEach((pane) => { pane.label = t(pane.label); });
  }
  for (const station of STATIONS) {
    station.label = t(station.label);
    station.blurb = t(station.blurb);
  }
}
